import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { decodeHtmlEntities } from '../../utils/displayText'

const STORAGE_START_KEY_PREFIX = 'rithmo_start_alerted_'
const AUTO_DISMISS_SECS = 300 // 5 minutes

function alreadyStartAlerted(meetingId) {
  try {
    return sessionStorage.getItem(STORAGE_START_KEY_PREFIX + meetingId) === '1'
  } catch {
    return false
  }
}

function markStartAlerted(meetingId) {
  try {
    sessionStorage.setItem(STORAGE_START_KEY_PREFIX + meetingId, '1')
  } catch {
    // sessionStorage may be unavailable in some privacy modes — fail silently
  }
}

export default function MeetingAlertOverlay() {
  const [meeting, setMeeting] = useState(null)
  const [visible, setVisible] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(AUTO_DISMISS_SECS)
  const timerRef = useRef(null)
  const countdownRef = useRef(null)
  const navigate = useNavigate()
  const location = useLocation()

  const dismiss = useCallback(() => {
    setVisible(false)
    window.dispatchEvent(new Event('rithmo:meeting-alert-dismissed'))
    clearTimeout(timerRef.current)
    clearInterval(countdownRef.current)
    // Wait for the CSS exit transition before removing the element
    setTimeout(() => setMeeting(null), 350)
  }, [])

  useEffect(() => {
    function handleStartAlert(event) {
      const m = event.detail
      if (!m) return
      const meetingId = m.id ?? m.meetingId
      if (alreadyStartAlerted(meetingId)) return
      markStartAlerted(meetingId)

      clearTimeout(timerRef.current)
      clearInterval(countdownRef.current)
      setMeeting(m)
      setSecondsLeft(AUTO_DISMISS_SECS)
      // Double rAF ensures the element is in the DOM before the CSS transition starts
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)))
      timerRef.current = setTimeout(dismiss, AUTO_DISMISS_SECS * 1000)
      countdownRef.current = setInterval(
        () => setSecondsLeft((s) => Math.max(0, s - 1)),
        1000
      )
    }

    window.addEventListener('rithmo:meeting-start-alert', handleStartAlert)
    return () => window.removeEventListener('rithmo:meeting-start-alert', handleStartAlert)
  }, [dismiss])

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current)
      clearInterval(countdownRef.current)
    }
  }, [])

  if (!meeting) return null

  const title = decodeHtmlEntities(meeting.title || 'Meeting')
  const isOnDashboard = location.pathname === '/dashboard'
  const progressPct = (secondsLeft / AUTO_DISMISS_SECS) * 100

  return (
    <div
      className="fixed bottom-6 right-6 z-[9999] w-80 max-w-[calc(100vw-3rem)] transition-all duration-300 ease-out pointer-events-auto"
      style={{
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(110%) scale(0.95)',
        opacity: visible ? 1 : 0,
      }}
    >
      <div className="bg-white rounded-xl overflow-hidden ring-2 ring-red-500 shadow-2xl">
        {/* Progress bar that drains to zero when auto-dismiss fires */}
        <div className="h-1 bg-gray-100">
          <div
            className="h-full bg-red-500 transition-[width] duration-1000 ease-linear"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Pulsing dot */}
            <div className="relative flex-shrink-0 mt-1">
              <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-red-500 uppercase tracking-wide leading-none">
                Starting Now
              </p>
              <p className="text-sm font-semibold text-gray-900 mt-1 leading-snug" title={title}>
                {title}
              </p>
            </div>
            <button
              onClick={dismiss}
              className="flex-shrink-0 -mt-0.5 text-gray-400 hover:text-gray-600 transition-colors text-xl leading-none"
              aria-label="Dismiss alert"
            >
              ×
            </button>
          </div>
          <div className="flex gap-2 mt-3">
            {!isOnDashboard && (
              <button
                onClick={() => { navigate('/dashboard'); dismiss() }}
                className="flex-1 px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                Open Rithmo
              </button>
            )}
            <button
              onClick={dismiss}
              className={`${isOnDashboard ? 'flex-1' : ''} px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors`}
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
